import React from 'react';
import { DialogActions, DialogContent, DialogTitle, IconButton, FormControl, Stack } from '@mui/material';
import { type FormSubmitButtonPresentation } from '../../molecules/form-elements/FormSubmitButton';
import { SkinnedFormSubmitButton } from '../../../config/skinRegistry/components';
import { FormControlProps } from '@mui/material';
import { ArrowBack, Close } from '@mui/icons-material';
import { mergeSx } from '../../../utils/styles';

export type StyledFormSubmitButtonPresentation = FormSubmitButtonPresentation;

type StyledFormProps = {
  title?: string;
  submitTitle: string;
  onSubmit: React.SubmitEventHandler<HTMLFormElement>;
  onClose?: () => void;
  onBack?: () => void;
  isSubmitting?: boolean;
  children: React.ReactNode;
  disablePadding?: boolean;
  sx?: FormControlProps['sx'];
  actionsSx?: FormControlProps['sx'];
  submitButton?: StyledFormSubmitButtonPresentation;
};

const makeStyles = (disablePadding: boolean) => ({
  dialogTitle: {
    typography: 'h3',
    m: 0,
    p: 4,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    right: 5,
    top: 5,
    color: 'neutral.300',
  },
  backButton: {
    position: 'absolute',
    left: 5,
    top: 5,
    color: 'neutral.300',
  },
  dialogContent: {
    p: disablePadding ? 0 : 3,
  },
  dialogActions: {
    display: 'flex',
    flexDirection: 'column',
    py: 2,
    px: disablePadding ? 0 : 3,
  },
});

const StyledForm = ({
  title,
  submitTitle,
  onSubmit,
  onClose,
  onBack,
  isSubmitting,
  children,
  disablePadding = false,
  sx,
  actionsSx,
  submitButton,
}: StyledFormProps) => {
  const styles = makeStyles(disablePadding);
  const showHeader = title || onClose || onBack;

  return (
    <FormControl component='form' onSubmit={onSubmit} sx={sx}>
      {showHeader && (
        <DialogTitle sx={styles.dialogTitle}>
          {onBack && (
            <IconButton onClick={onBack} sx={styles.backButton} disabled={isSubmitting}>
              <ArrowBack />
            </IconButton>
          )}
          {title}
          {onClose && (
            <IconButton onClick={onClose} sx={styles.closeButton} disabled={isSubmitting}>
              <Close />
            </IconButton>
          )}
        </DialogTitle>
      )}
      <DialogContent sx={styles.dialogContent}>
        <Stack
          spacing={1.5}
          sx={{
            py: 1,
          }}
        >
          {children}
        </Stack>
      </DialogContent>
      <DialogActions sx={mergeSx(styles.dialogActions, actionsSx)}>
        <SkinnedFormSubmitButton isSubmitting={isSubmitting} title={submitTitle} {...submitButton} />
      </DialogActions>
    </FormControl>
  );
};

export default StyledForm;
